import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Table, TableBody, TableCell,
  TableHead, TableRow, CircularProgress, Alert, Collapse, IconButton, Divider, Tooltip,
  Card, CardContent,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { apiService } from '../services/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { BUILD_TIME, BUILD_SHA } from '../buildInfo';

const usd = (v: any) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const pct = (v: any) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(2)}%`);
const pnlColor = (v: any) => (v > 0 ? '#22C55E' : v < 0 ? '#EF4444' : 'inherit');

// SPY vs its 200-day average and 10-month trend on the day (simulation_service.market_state)
const MARKET_COLOR: Record<string, string> = { rising: '#22C55E', falling: '#EF4444', mixed: '#F59E0B' };
const actionColor: Record<string, string> = {
  BUY: '#22C55E', SELL: '#EF4444', TRIM: '#F59E0B',
};

function Stat({ label, value, color, sub }: any) {
  return (
    <Paper sx={{ p: 1.5, minWidth: 150, flex: '1 1 auto' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={700} sx={{ color: color || 'inherit' }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
    </Paper>
  );
}


/**
 * Equity against the two things the owner could have done instead: bought the index,
 * or held T-bills. Same starting capital, same start date, marked on the same days.
 * This is the comparison the simulation exists to make, and the one that was missing
 * when the question "would SPY or bonds have done better?" was first asked.
 */
function EquityChart() {
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    apiService.getSimulationEquityCurve()
      .then(setData).catch(e => setErr(e?.response?.data?.detail || e.message));
  }, []);
  if (err) return <Alert severity="info" sx={{ mb: 2 }}>{err}</Alert>;
  if (!data?.points?.length) return null;
  const s = data.summary;
  const usdc = (v: number) => `$${Math.round(v).toLocaleString()}`;
  const lead = (pts: number | null) =>
    pts == null ? null
      : <Box component="span" sx={{ color: pts >= 0 ? 'success.main' : 'error.main', fontWeight: 700 }}>
          {pts >= 0 ? '+' : ''}{pts.toFixed(2)} pts
        </Box>;
  return (
    <Card sx={{ mb: 2 }}><CardContent>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold">
          Equity vs buying the index vs holding T-bills
        </Typography>
        <Typography variant="caption" color="text.secondary">
          all three start at {usdc(s.starting_balance)} on {s.start_date} · T-bills = BIL (1–3 month)
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography variant="body2">Portfolio <b>{usdc(s.portfolio)}</b> ({s.portfolio_pct >= 0 ? '+' : ''}{s.portfolio_pct}%)</Typography>
        <Typography variant="body2">SPY <b>{usdc(s.spy)}</b> ({s.spy_pct >= 0 ? '+' : ''}{s.spy_pct}%) · vs SPY {lead(s.vs_spy_pts)}</Typography>
        <Typography variant="body2">T-bills <b>{usdc(s.tbill)}</b> ({s.tbill_pct >= 0 ? '+' : ''}{s.tbill_pct}%) · vs T-bills {lead(s.vs_tbill_pts)}</Typography>
      </Box>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data.points} margin={{ left: 8, right: 16, top: 6, bottom: 4 }}>
          <CartesianGrid strokeDasharray="2 4" opacity={0.25} />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={36} />
          <YAxis tick={{ fontSize: 10 }} width={70} domain={['auto', 'auto']}
                 tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
          <RTooltip contentStyle={{ fontSize: 12 }}
                    formatter={(v: any, n: string) => [typeof v === 'number' ? usdc(v) : v, n]} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="portfolio" name="Portfolio" stroke="#60A5FA" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="spy" name="SPY buy & hold" stroke="#10B981" strokeWidth={1.6} dot={false} strokeDasharray="5 3" connectNulls />
          <Line type="monotone" dataKey="tbill" name="T-bills (BIL)" stroke="#F59E0B" strokeWidth={1.6} dot={false} strokeDasharray="2 3" connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </CardContent></Card>
  );
}

function EvidencePortfolio() {
  const [d, setD] = useState<any>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { apiService.getEvidencePortfolio().then(setD).catch(() => undefined); }, []);
  if (!d) return null;
  const design = d.replay?.['70% SPY + 30% calmer momentum'];
  const spy = d.replay?.SPY;
  const last = d.curve?.[d.curve.length - 1];
  const usdc = (v: number) => `$${Math.round(v).toLocaleString()}`;
  const years = design ? Object.keys(design.years) : [];
  return (
    <Card sx={{ mb: 2, borderLeft: '4px solid #A78BFA' }}><CardContent>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold">Second portfolio (paper): index core + momentum</Typography>
        <Chip size="small" label="forward test" variant="outlined" />
        <Typography variant="caption" color="text.secondary">{d.rule}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        The design the Signal Lab measured best, run beside this simulator without changing it. In the
        {' '}{d.replay_from?.slice(0, 7)} → {d.replay_to?.slice(0, 7)} replay it returned <b>{design?.cagr_pct}%</b> a year against
        SPY's {spy?.cagr_pct}% (worst fall {design?.worst_fall_pct}% vs {spy?.worst_fall_pct}%), ahead in both halves — but the
        momentum rule was chosen on those same years and the edge is not statistically proven (t {design?.vs_spy_t}).
        From here on it earns a real record.
      </Typography>
      {last ? (
        <>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="body2">Since {d.started_on}: <b>{usdc(last.equity)}</b> ({pct((last.equity / 100000 - 1) * 100)})</Typography>
            <Typography variant="body2">SPY on the same money <b>{usdc(last.spy)}</b> ({pct((last.spy / 100000 - 1) * 100)})</Typography>
            <Typography variant="body2">Last rebalance {d.last_rebalance}</Typography>
          </Box>
          {d.curve.length > 1 && (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={d.curve} margin={{ left: 8, right: 16, top: 6, bottom: 4 }}>
                <CartesianGrid strokeDasharray="2 4" opacity={0.25} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={36} />
                <YAxis tick={{ fontSize: 10 }} width={70} domain={['auto', 'auto']} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                <RTooltip contentStyle={{ fontSize: 12 }} formatter={(v: any, n: string) => [typeof v === 'number' ? usdc(v) : v, n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="equity" name="Core + momentum" stroke="#A78BFA" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="spy" name="SPY" stroke="#10B981" strokeWidth={1.6} dot={false} strokeDasharray="5 3" />
              </LineChart>
            </ResponsiveContainer>
          )}
          <Button size="small" onClick={() => setOpen(!open)} endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}>
            Holdings ({d.holdings?.length})
          </Button>
          <Collapse in={open}>
            <Table size="small">
              <TableHead><TableRow><TableCell>Stock</TableCell><TableCell align="right">Weight</TableCell><TableCell align="right">Value</TableCell></TableRow></TableHead>
              <TableBody>
                {d.holdings.map((h: any) => (
                  <TableRow key={h.symbol}><TableCell>{h.symbol}</TableCell><TableCell align="right">{h.weight_pct}%</TableCell><TableCell align="right">{usd(h.value)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </>
      ) : (
        <Alert severity="info" sx={{ py: 0 }}>
          Starts with $100,000 on the first session after the next month-end momentum book (the first book is formed after
          the close of a month's last session).
        </Alert>
      )}
      {design && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Replay by year — design / SPY: {years.map(y => `${y} ${design.years[y]}/${spy?.years?.[y]}`).join(' · ')}
        </Typography>
      )}
    </CardContent></Card>
  );
}

function DayRow({ d }: { d: any }) {
  const [open, setOpen] = useState(false);
  // Arrays, or nothing. A malformed row (candidates arrived as an object once) must
  // degrade to an empty list, not take the whole page down with "map is not a function".
  const arr = (x: any) => (Array.isArray(x) ? x : []);
  const acts = arr(d.actions);
  const skipped = arr(d.candidates_skipped);
  const holdings = arr(d.holdings);
  return (
    <>
      <TableRow hover>
        <TableCell width={40}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>
          {d.date}
          {d.notes?.market?.label && d.notes.market.label !== 'unknown' && (
            <Tooltip title={`SPY ${d.notes.market.spy_vs_200d_pct > 0 ? '+' : ''}${d.notes.market.spy_vs_200d_pct}% vs its 200-day average; 10-month trend ${d.notes.market.trend_10m_up ? 'up' : 'down'}`}>
              <Chip size="small" label={`market ${d.notes.market.label}`}
                sx={{ ml: 1, height: 18, fontSize: '0.65rem', color: MARKET_COLOR[d.notes.market.label], bgcolor: `${MARKET_COLOR[d.notes.market.label]}22` }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="right">{usd(d.total_equity)}</TableCell>
        <TableCell align="right">{usd(d.cash)}</TableCell>
        <TableCell align="right">
          <Tooltip title={holdings.length
            ? holdings.map((h: any) => `${h.symbol} ${h.shares}sh`).join(' · ')
            : 'nothing held'}>
            <span>{d.position_count ?? 0}</span>
          </Tooltip>
          {(d.holdings || []).length > 0 && (
            <Typography variant="caption" display="block" color="text.disabled">
              {holdings.map((h: any) => `${h.symbol} ${h.shares}sh`).join(', ')}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {acts.length === 0
            ? <Typography variant="caption" color="text.disabled">no action</Typography>
            : acts.map((a: any, i: number) => (
                <Chip key={i} size="small" label={`${a.action} ${a.symbol}`}
                  sx={{ mr: 0.5, height: 20, bgcolor: `${actionColor[a.action]}22`, color: actionColor[a.action] }} />
              ))}
        </TableCell>
        <TableCell align="right">
          <Typography variant="caption" color="text.disabled">{skipped.length} skipped</Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: '#8881', borderRadius: 1, mb: 1 }}>
              {acts.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Actions taken</Typography>
                  {acts.map((a: any, i: number) => (
                    <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                      <b style={{ color: actionColor[a.action] }}>{a.action}</b>{' '}
                      {a.shares} × {a.symbol} @ {usd(a.price)}
                      {a.conviction != null && ` · conviction ${a.conviction}`}
                      {a.return_pct != null && ` · ${pct(a.return_pct)} in ${a.days_held}d`}
                      {a.reason && ` · ${a.reason}`}
                      {a.shares_remaining != null && ` · ${a.shares_remaining} left running`}
                      {a.action === 'BUY' && a.expires_on && (
                        <Typography variant="caption" display="block" sx={{ color: '#22D3EE' }}>
                          {a.strategy && `${a.strategy}: `}
                          {a.stop_price != null ? `stop ${usd(a.stop_price)}` : 'no stop'}
                          {' · '}{a.target_price != null ? `target ${usd(a.target_price)}` : 'no price target'}
                          {' · '}expires {a.expires_on}
                          {a.market && ` · bought in a ${a.market} market`}
                        </Typography>
                      )}
                      {(a.why || a.exit_note) && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {a.why || a.exit_note}
                        </Typography>
                      )}
                    </Typography>
                  ))}
                  <Divider sx={{ my: 1 }} />
                </>
              )}
              <Typography variant="subtitle2" gutterBottom>
                Considered but skipped — why the rules rejected them
              </Typography>
              {skipped.length === 0
                ? <Typography variant="caption" color="text.disabled">nothing skipped</Typography>
                : skipped.map((c: any, i: number) => (
                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                      {c.symbol} (conviction {c.conviction}, {c.signal_count} signals) — {c.skipped}
                    </Typography>
                  ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function BreakdownTable({ title, data, hint }: any) {
  if (!data || Object.keys(data).length === 0) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
      {hint && <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{hint}</Typography>}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell align="right">n</TableCell>
            <TableCell align="right">Win rate</TableCell>
            <TableCell align="right">Avg return</TableCell>
            <TableCell align="right">Median</TableCell>
            <TableCell align="right">Total P&L</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(data).map(([k, v]: any) => (
            <TableRow key={k} sx={{ opacity: v.reliable ? 1 : 0.55 }}>
              <TableCell sx={{ fontWeight: 600 }}>
                {k}
                {!v.reliable && <Tooltip title="Fewer than 20 trades — treat as noise">
                  <Chip size="small" label="low n" sx={{ ml: 1, height: 16, fontSize: '0.65rem' }} />
                </Tooltip>}
              </TableCell>
              <TableCell align="right">{v.n}</TableCell>
              <TableCell align="right">{v.win_rate_pct}%</TableCell>
              <TableCell align="right" sx={{ color: pnlColor(v.avg_return_pct) }}>{pct(v.avg_return_pct)}</TableCell>
              <TableCell align="right" sx={{ color: pnlColor(v.median_return_pct) }}>{pct(v.median_return_pct)}</TableCell>
              <TableCell align="right" sx={{ color: pnlColor(v.total_pnl), fontWeight: 700 }}>{usd(v.total_pnl)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function Simulation() {
  const [tab, setTab] = useState(0);
  const [snap, setSnap] = useState<any>(null);
  const [log, setLog] = useState<any>(null);
  const [trades, setTrades] = useState<any>(null);
  const [perf, setPerf] = useState<any>(null);
  const [findings, setFindings] = useState<any>(null);
  const [ledger, setLedger] = useState<any>(null);
  const [recon, setRecon] = useState<any>(null);
  const [improve, setImprove] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const arguments0 = await Promise.all([
        apiService.getSimulation(), apiService.getSimulationLog(),
        apiService.getSimulationTrades(), apiService.getSimulationPerformance(),
        apiService.getEngineFindings(), apiService.getSimulationLedger(),
        apiService.reconcileSimulation(), apiService.getSimulationImprovements(),
      ]);
      const [s, l, t, p, f, lg, rc, im] = arguments0;
      setSnap(s); setLog(l); setTrades(t); setPerf(p); setFindings(f);
      setLedger(lg); setRecon(rc); setImprove(im);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const runSession = async () => {
    setBusy(true); setError(null);
    try { await apiService.runSimulation(); await loadAll(); }
    catch (e: any) { setError(e?.response?.data?.detail || e.message || 'Run failed'); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset the simulation to $100,000 and delete all trades and logs?')) return;
    setBusy(true);
    try { await apiService.resetSimulation(); await loadAll(); }
    catch (e: any) { setError(e?.message || 'Reset failed'); }
    finally { setBusy(false); }
  };

  const r = snap?.rules;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" fontWeight={700}>Simulation</Typography>
        <Chip size="small" label={`build ${BUILD_TIME} · ${BUILD_SHA}`}
          sx={{ height: 20, fontSize: '0.65rem', opacity: 0.6 }} />
        <Button variant="contained" onClick={runSession} disabled={busy}>
          {busy ? <CircularProgress size={20} /> : 'Run session'}
        </Button>
        <Button variant="outlined" onClick={loadAll} disabled={loading}>Refresh</Button>
        <Button variant="text" color="error" onClick={reset} disabled={busy}>Reset</Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Paper trading that follows the <b>Action Board</b> mechanically on ${'{'}100,000{'}'} — no leverage,
        cash is a hard limit. It exists to answer <b>which recommendations actually work</b>, so every
        decision stores the signals behind it. Separate from the Trade Journal, which is what you really did.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {snap && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Stat label="Total equity" value={usd(snap.total_equity)}
            color={pnlColor(snap.total_equity - snap.starting_balance)}
            sub={`${pct(snap.total_return_pct)} since ${snap.started_on}`} />
          <Stat label="Cash" value={usd(snap.cash)} sub={`${snap.open_positions?.length || 0} positions open`} />
          <Stat label="Positions value" value={usd(snap.positions_value)} />
          <Stat label="Realized P&L" value={usd(snap.realized_pnl)} color={pnlColor(snap.realized_pnl)}
            sub={`${snap.closed_trades} closed`} />
          <Stat label="Unrealized P&L" value={usd(snap.unrealized_pnl)} color={pnlColor(snap.unrealized_pnl)} />
          <Stat label="Win rate" value={snap.win_rate_pct != null ? `${snap.win_rate_pct}%` : '—'} />
        </Box>
      )}

      {r && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Rules — buys need conviction ≥ {r.min_conviction} and ≥ {r.min_signal_count} signals ·
          max {r.max_positions} positions at {r.position_size_pct}% of {r.position_size_basis} each ·
          stop {r.stop_loss_pct}% · take profit +{r.take_profit_pct}% · max hold {r.max_hold_days}d ·
          SELL closes, TRIM banks half
          <br />Commissions: {r.commission_model}
        </Typography>
      )}

      <EquityChart />
      <EvidencePortfolio />

      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Daily journal" />
        <Tab label="Open positions" />
        <Tab label="Closed trades" />
        <Tab label="What's working" />
        <Tab label="Engine findings" />
        <Tab label="Ledger" />
        <Tab label="Improvements" />
      </Tabs>

      {tab === 0 && (
        <Paper sx={{ p: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Date</TableCell>
                <TableCell align="right">Equity</TableCell>
                <TableCell align="right">Cash</TableCell>
                <TableCell align="right">Open&nbsp;positions</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {log?.days?.length
                ? log.days.map((d: any) => <DayRow key={d.date} d={d} />)
                : <TableRow><TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No sessions yet — hit “Run session”.
                    </Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell><TableCell align="right">Shares</TableCell>
                <TableCell align="right">Entry</TableCell><TableCell align="right">Now</TableCell>
                <TableCell align="right">Value</TableCell><TableCell align="right">P&L</TableCell>
                <TableCell align="right">Return</TableCell><TableCell align="right">Held</TableCell>
                <TableCell>Why it was bought</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {snap?.open_positions?.length
                ? snap.open_positions.map((p: any) => (
                    <TableRow key={p.symbol} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{p.symbol}</TableCell>
                      <TableCell align="right">{p.shares}</TableCell>
                      <TableCell align="right">{usd(p.entry_price)}</TableCell>
                      <TableCell align="right">{usd(p.current_price)}</TableCell>
                      <TableCell align="right">{usd(p.market_value)}</TableCell>
                      <TableCell align="right" sx={{ color: pnlColor(p.unrealized_pnl) }}>{usd(p.unrealized_pnl)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: pnlColor(p.return_pct) }}>{pct(p.return_pct)}</TableCell>
                      <TableCell align="right">{p.days_held}d</TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          conviction {p.entry_signals?.conviction} · score {p.entry_signals?.engine_score ?? '—'}
                          {' · '}{p.entry_signals?.theme || '—'}
                        </Typography>
                        {p.entry_signals?.strategy && (
                          <Typography variant="caption" display="block" sx={{ color: '#22D3EE' }}>
                            {p.entry_signals.strategy.label} — hold {p.entry_signals.strategy.max_hold_days}d,
                            stop {p.entry_signals.strategy.stop_loss_pct}%,
                            {p.entry_signals.strategy.take_profit_pct == null
                              ? ' no profit target (thesis)'
                              : ` target +${p.entry_signals.strategy.take_profit_pct}%`}
                          </Typography>
                        )}
                        {p.exit_plan && (
                          <Typography variant="caption" display="block">
                            Ends at {p.exit_plan.stop_price != null ? `${usd(p.exit_plan.stop_price)} (stop)` : 'no stop'}
                            {p.exit_plan.target_price != null && ` or ${usd(p.exit_plan.target_price)} (target)`}
                            {' '}or on <b>{p.exit_plan.expires_on}</b>
                            {p.market_at_entry?.label && p.market_at_entry.label !== 'unknown' && ` · bought in a ${p.market_at_entry.label} market`}
                          </Typography>
                        )}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {p.entry_signals?.why}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                : <TableRow><TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary">No open positions.</Typography>
                  </TableCell></TableRow>}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 2 && (
        <Paper sx={{ p: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell><TableCell align="right">Shares</TableCell>
                <TableCell align="right">Entry</TableCell><TableCell align="right">Exit</TableCell>
                <TableCell align="right">Return</TableCell><TableCell align="right">P&L</TableCell>
                <TableCell align="right">Held</TableCell><TableCell>Exit reason</TableCell>
                <TableCell>Entry conviction</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trades?.trades?.length
                ? trades.trades.map((t: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{t.symbol}</TableCell>
                      <TableCell align="right">{t.shares}</TableCell>
                      <TableCell align="right">{usd(t.entry_price)}<Typography variant="caption" display="block" color="text.disabled">{t.entry_date}</Typography></TableCell>
                      <TableCell align="right">{usd(t.exit_price)}<Typography variant="caption" display="block" color="text.disabled">{t.exit_date}</Typography></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: pnlColor(t.return_pct) }}>{pct(t.return_pct)}</TableCell>
                      <TableCell align="right" sx={{ color: pnlColor(t.net_pnl) }}>{usd(t.net_pnl)}</TableCell>
                      <TableCell align="right">{t.days_held}d</TableCell>
                      <TableCell><Chip size="small" label={t.exit_reason} sx={{ height: 20 }} /></TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {t.entry_signals?.conviction ?? '—'} · {t.entry_signals?.theme || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                : <TableRow><TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary">No closed trades yet.</Typography>
                  </TableCell></TableRow>}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 3 && (
        <Paper sx={{ p: 2 }}>
          {!perf || perf.closed_trades === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {perf?.note || 'No closed trades yet — run the simulation for a few sessions.'}
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                <Stat label="Closed trades" value={perf.closed_trades} />
                <Stat label="Win rate" value={`${perf.overall?.win_rate_pct}%`} />
                <Stat label="Avg return" value={pct(perf.overall?.avg_return_pct)} color={pnlColor(perf.overall?.avg_return_pct)} />
                <Stat label="Median" value={pct(perf.overall?.median_return_pct)} color={pnlColor(perf.overall?.median_return_pct)} />
                <Stat label="Best / worst" value={`${pct(perf.best_trade_pct)} / ${pct(perf.worst_trade_pct)}`} />
              </Box>

              <BreakdownTable title="By conviction band" data={perf.by_conviction}
                hint="If higher conviction doesn't produce better returns, the score isn't carrying information." />
              <BreakdownTable title="By signal count" data={perf.by_signal_count}
                hint="If 3 signals doesn't beat 2, consensus isn't adding value." />
              <BreakdownTable title="By theme" data={perf.by_theme}
                hint="Is one theme carrying the whole result?" />
              <BreakdownTable title="By exit reason" data={perf.by_exit_reason}
                hint="stop_loss dominating means entries are too early or stops too tight." />

              <Divider sx={{ my: 2 }} />
              {perf.how_to_read?.map((h: string, i: number) => (
                <Typography key={i} variant="caption" display="block" color="text.secondary">• {h}</Typography>
              ))}
            </>
          )}
        </Paper>
      )}

      {tab === 4 && (
        <Paper sx={{ p: 2 }}>
          {!findings || findings.closed_trades === 0 ? (
            <Alert severity="info">{findings?.note || 'No closed trades yet.'}</Alert>
          ) : (
            <>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                How each engine performed, by horizon
              </Typography>
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Engine</TableCell>
                    <TableCell align="right">Trades</TableCell>
                    <TableCell align="right">Win rate</TableCell>
                    <TableCell align="right">Median</TableCell>
                    <TableCell align="right">Profit factor</TableCell>
                    <TableCell align="right">Avg conviction</TableCell>
                    <TableCell align="right">1w</TableCell>
                    <TableCell align="right">1m</TableCell>
                    <TableCell align="right">6m</TableCell>
                    <TableCell align="right">1y</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(findings.engines || {}).map(([name, e]: any) => (
                    <TableRow key={name} sx={{ opacity: e.reliable ? 1 : 0.55 }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {name}
                        {!e.reliable && <Chip size="small" label="low n" sx={{ ml: 1, height: 16, fontSize: '0.65rem' }} />}
                      </TableCell>
                      <TableCell align="right">{e.n}</TableCell>
                      <TableCell align="right">{e.win_rate_pct}%</TableCell>
                      <TableCell align="right" sx={{ color: pnlColor(e.median_return_pct) }}>{pct(e.median_return_pct)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: (e.profit_factor ?? 0) >= 1 ? '#22C55E' : '#EF4444' }}>
                        {e.profit_factor ?? '—'}
                      </TableCell>
                      <TableCell align="right">{e.avg_conviction ?? '—'}</TableCell>
                      {['1w', '1m', '6m', '1y'].map((h) => {
                        const v = e.by_horizon?.[h];
                        return (
                          <TableCell key={h} align="right">
                            {v?.n ? (
                              <Tooltip title={`${v.n} trades, ${v.win_rate_pct}% win`}>
                                <span style={{ color: pnlColor(v.median_return_pct) }}>{pct(v.median_return_pct)}</span>
                              </Tooltip>
                            ) : <span style={{ opacity: 0.4 }}>—</span>}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                What to change
              </Typography>
              {findings.findings?.map((f: any, i: number) => {
                const sev: Record<string, any> = {
                  critical: { c: 'error', t: 'LOSING MONEY' },
                  underperforming: { c: 'warning', t: 'BELOW BASELINE' },
                  outperforming: { c: 'success', t: 'BEATING BASELINE' },
                  tune_exits: { c: 'warning', t: 'EXIT RULES' },
                  horizon_mismatch: { c: 'warning', t: 'WRONG HOLDING PERIOD' },
                  calibration: { c: 'warning', t: 'CONVICTION MISCALIBRATED' },
                  insufficient_data: { c: 'info', t: 'NOT ENOUGH DATA' },
                };
                const s2 = sev[f.severity] || { c: 'info', t: f.severity };
                return (
                  <Alert key={i} severity={s2.c} sx={{ mb: 1 }}>
                    <Typography variant="caption" fontWeight={700}>{s2.t} · {f.engine}
                      {f.n != null && ` · n=${f.n}`}
                    </Typography>
                    <Typography variant="body2">{f.finding}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>→ {f.suggestion}</Typography>
                  </Alert>
                );
              })}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                Engines with fewer than {findings.min_reliable_n} closed trades are reported as
                insufficient data rather than acted on — a small sample read as a result is the
                mistake this whole simulation exists to avoid.
              </Typography>
            </>
          )}
        </Paper>
      )}


      {tab === 5 && (
        <Paper sx={{ p: 2 }}>
          {recon && (
            <Alert severity={recon.balanced ? 'success' : 'error'} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={700}>
                {recon.balanced ? 'Balance verified' : 'BALANCE DOES NOT RECONCILE'}
              </Typography>
              <Typography variant="caption" display="block">
                Reported {usd(recon.cash?.reported)} · ledger running balance {usd(recon.cash?.ledger_running_balance)} ·
                sum of all movements {usd(recon.cash?.ledger_sum_from_start)} · discrepancy ${recon.cash?.max_discrepancy}
              </Typography>
              <Typography variant="caption" display="block">
                {recon.coverage?.open_positions} open position(s) with {recon.coverage?.symbols_with_buy_entries} BUY entries ·
                {' '}{recon.coverage?.closed_trades} closed trade(s) with {recon.coverage?.sell_trim_ledger_entries} SELL/TRIM entries
              </Typography>
              {recon.issues?.length > 0 && recon.issues.map((i: string, k: number) => (
                <Typography key={k} variant="body2" sx={{ mt: 0.5 }}>• {i}</Typography>
              ))}
            </Alert>
          )}

          {ledger && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
              <Stat label="Starting balance" value={usd(ledger.starting_balance)} />
              <Stat label="Total invested" value={usd(ledger.total_invested)} />
              <Stat label="Total proceeds" value={usd(ledger.total_proceeds)} />
              <Stat label="Commissions paid" value={usd(ledger.total_commissions)} color="#EF4444" />
              <Stat label="Transactions" value={ledger.transaction_count} />
              <Stat label="Current equity" value={usd(recon?.equity?.total)}
                sub={`${usd(recon?.equity?.cash)} cash + ${usd(recon?.equity?.positions_value)} positions`} />
            </Box>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell>Type</TableCell><TableCell>Symbol</TableCell>
                <TableCell align="right">Shares</TableCell><TableCell align="right">Price</TableCell>
                <TableCell align="right">Gross</TableCell><TableCell align="right">Commission</TableCell>
                <TableCell align="right">Net</TableCell><TableCell align="right">Balance after</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ledger?.entries?.length
                ? ledger.entries.map((e: any) => (
                    <TableRow key={e.id} hover>
                      <TableCell>{e.date}</TableCell>
                      <TableCell>
                        <Chip size="small" label={e.type} sx={{ height: 20,
                          bgcolor: `${actionColor[e.type] || '#888'}22`,
                          color: actionColor[e.type] || 'inherit' }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{e.symbol || '—'}</TableCell>
                      <TableCell align="right">{e.shares ?? '—'}</TableCell>
                      <TableCell align="right">{e.price != null ? usd(e.price) : '—'}</TableCell>
                      <TableCell align="right" sx={{ color: pnlColor(e.gross_amount) }}>{usd(e.gross_amount)}</TableCell>
                      <TableCell align="right" sx={{ color: '#EF4444' }}>{usd(e.commission)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: pnlColor(e.net_amount) }}>{usd(e.net_amount)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{usd(e.balance_after)}</TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{e.note}</Typography></TableCell>
                    </TableRow>
                  ))
                : <TableRow><TableCell colSpan={10}>
                    <Typography variant="body2" color="text.secondary">No transactions yet.</Typography>
                  </TableCell></TableRow>}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Every cash movement is recorded here with the resulting balance. Positions and trades
            are derived views; this ledger is the record that has to add up, and the banner above
            recomputes it three independent ways to prove it does.
          </Typography>
        </Paper>
      )}

      {tab === 6 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            What the record says to change
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Closed trades read over 1-month, 3-month, 6-month and 1-year windows. Engines are
            judged against <strong>the portfolio's own win rate over the same window</strong> —
            not against 50% — because the real alternative is picking from the rest of the book.
            Nothing under {improve?.min_trades ?? 20} trades in a window becomes advice.
            Recomputed monthly, and on every load of this tab.
          </Typography>

          {improve && (
            <Alert severity={improve.ready ? (improve.recommendations?.length ? 'warning' : 'success') : 'info'}
              sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={700}>{improve.headline}</Typography>
              {!improve.ready && (
                <Typography variant="caption" display="block">
                  The simulation trades weekdays at 17:15 ET. Until the windows fill, this stays
                  quiet on purpose — a small sample read as a result has already produced three
                  reversals in this project.
                </Typography>
              )}
            </Alert>
          )}

          {improve?.recommendations?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {improve.recommendations.map((r: any, i: number) => (
                <Card key={i} sx={{ mb: 1.5, borderLeft: `4px solid ${
                  r.severity === 'high' ? '#EF4444'
                  : r.severity === 'medium' ? '#F59E0B'
                  : r.severity === 'opportunity' ? '#22C55E' : '#6B7280'}` }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                      <Chip size="small" label={r.severity.toUpperCase()}
                        sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
                      <Chip size="small" label={r.area} variant="outlined"
                        sx={{ height: 18, fontSize: '0.6rem' }} />
                      <Chip size="small" label={r.window} variant="outlined"
                        sx={{ height: 18, fontSize: '0.6rem' }} />
                      {r.corroborated_in?.length > 0 && (
                        <Tooltip title="The same conclusion holds in these other windows, so it is a property of the engine rather than a market regime.">
                          <Chip size="small" label={`also in ${r.corroborated_in.join(', ')}`}
                            sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#3B82F622', color: '#3B82F6' }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="body2" fontWeight={600}>{r.finding}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      → {r.suggestion}
                    </Typography>
                    {r.evidence && (
                      <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                        evidence: {Object.entries(r.evidence).map(([k, v]) => `${k}=${v}`).join(' · ')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            By window
          </Typography>
          <Table size="small">
            <TableHead><TableRow>
              {['Window', 'Trades', 'Win rate', 'Median', 'Profit factor', 'Total P&L'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
              ))}
            </TableRow></TableHead>
            <TableBody>
              {improve?.windows && Object.entries(improve.windows).map(([w, d]: any) => (
                <TableRow key={w} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{w}</TableCell>
                  <TableCell sx={{ color: (d.overall?.n ?? 0) < (improve.min_trades ?? 20)
                    ? 'text.disabled' : 'text.primary' }}>
                    {d.overall?.n ?? 0}
                  </TableCell>
                  <TableCell>{d.overall?.win_rate_pct != null ? `${d.overall.win_rate_pct}%` : '—'}</TableCell>
                  <TableCell sx={{ color: (d.overall?.median_return_pct ?? 0) >= 0 ? '#22C55E' : '#EF4444' }}>
                    {d.overall?.median_return_pct != null ? `${d.overall.median_return_pct}%` : '—'}
                  </TableCell>
                  <TableCell>{d.overall?.profit_factor ?? '—'}</TableCell>
                  <TableCell>{d.overall?.total_pnl != null ? usd(d.overall.total_pnl) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            A finding that appears in the 1-month window <em>and</em> the 6-month one is a
            different claim from one that only shows up short-term — the first is a property of
            the engine, the second is probably a market regime. That is what "also in" marks.
          </Typography>
        </Paper>
      )}

    </Box>
  );
}
